import { useTranslation } from 'react-i18next';
import { CoverageRailShell, type CoverageRailBucket } from './CoverageRailShell';
import { CoverageBar, RailCodeTile, RailDefectBadge } from './CoverageRailBits';
import type { CoverageBoardVM } from './useCoverageBoard';
import type { AppCoverageRow, CoverageTab } from './types';

/**
 * EAA-1817: the **Theo ứng dụng** rail — one row per registry app, with its
 * coverage against the standard-role vocabulary (frame 55, left column).
 *
 * EAA-1817 (2026-08-24): the chrome moved to `CoverageRailShell` and the pills
 * and rows to the kit's `FilterPill`/`Item`. What is left here is the only part
 * that was ever about applications: which buckets exist, and what one row says.
 */
export function AppCoverageRail({ vm }: { vm: CoverageBoardVM }) {
    const { t } = useTranslation();

    const dotStatusText: Record<AppCoverageRow['dotTone'], string> = {
        ok: t('console.roleCoverage.rail.dotStatus.ok'),
        warn: t('console.roleCoverage.rail.dotStatus.warn'),
        off: t('console.roleCoverage.rail.dotStatus.off'),
        muted: t('console.roleCoverage.rail.dotStatus.muted'),
    };

    const buckets: CoverageRailBucket<CoverageTab>[] = [
        { key: 'all', label: t('console.roleCoverage.rail.tabs.all'), count: vm.appRows.length },
        {
            key: 'needsAttention',
            label: t('console.roleCoverage.rail.tabs.needsAttention'),
            count: vm.needsAttentionCount,
        },
        {
            key: 'uncovered',
            label: t('console.roleCoverage.rail.tabs.uncovered'),
            count: vm.uncoveredCount,
        },
    ];

    const linkedPairs = vm.appRows.reduce((total, row) => total + row.coveredCount, 0);
    const totalPairs = vm.appRows.length * (vm.appRows[0]?.totalRoleKeys ?? 0);

    return (
        <CoverageRailShell<CoverageTab>
            title={t('console.roleCoverage.rail.title', { count: vm.appRows.length })}
            ariaLabel={t('console.roleCoverage.rail.ariaLabel')}
            searchPlaceholder={t('console.roleCoverage.rail.filterPlaceholder')}
            searchAriaLabel={t('console.roleCoverage.rail.filterAriaLabel')}
            search={vm.search}
            onSearchChange={vm.setSearch}
            bucketsAriaLabel={t('console.roleCoverage.rail.tabsAriaLabel')}
            buckets={buckets}
            activeBucket={vm.tab}
            onBucketChange={vm.setTab}
            selectedKey={vm.selectedAppCode}
            onSelect={vm.selectApp}
            noMatchText={t('console.roleCoverage.rail.noMatch')}
            footer={t('console.roleCoverage.rail.footer', {
                appCount: vm.appRows.length,
                linked: linkedPairs,
                total: totalPairs,
            })}
            rows={vm.visibleRows.map((row) => ({
                key: row.code,
                tile: <RailCodeTile code={row.code} />,
                title: row.name,
                caption: <CoverageBar covered={row.coveredCount} total={row.totalRoleKeys} />,
                trailing: (
                    <RailDefectBadge
                        defectCount={row.pausedCellCount}
                        covered={row.coveredCount}
                        total={row.totalRoleKeys}
                    />
                ),
                ariaLabel: t('console.roleCoverage.rail.rowAriaLabel', {
                    name: row.name,
                    covered: row.coveredCount,
                    total: row.totalRoleKeys,
                    statusText: dotStatusText[row.dotTone],
                }),
            }))}
        />
    );
}

export default AppCoverageRail;
